// Service de gestion des notifications
class NotificationService {
  constructor() {
    this.notifications = [];
    this.listeners = [];
  }

  // Ajouter une notification
  addNotification(notification) {
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      read: false,
      ...notification
    };

    this.notifications.unshift(newNotification);
    this.notifyListeners();
    
    return newNotification.id;
  }

  // Types de notifications
  success(message, title = "Succès") {
    return this.addNotification({
      type: "success",
      title,
      message,
      icon: "✅",
      autoHide: true,
      duration: 5000
    });
  }

  error(message, title = "Erreur") {
    return this.addNotification({
      type: "error",
      title,
      message,
      icon: "❌",
      autoHide: false
    });
  }

  warning(message, title = "Attention") {
    return this.addNotification({
      type: "warning",
      title,
      message,
      icon: "⚠️",
      autoHide: true,
      duration: 7000
    });
  }

  info(message, title = "Information") {
    return this.addNotification({
      type: "info",
      title,
      message,
      icon: "ℹ️",
      autoHide: true,
      duration: 6000
    });
  }

  // Notifications spécifiques métiers
  reservationConfirmed(reservationData) {
    return this.addNotification({
      type: "success",
      title: "Réservation confirmée",
      message: `Votre rendez-vous avec ${reservationData.artisan} est confirmé le ${reservationData.date} à ${reservationData.heure}`,
      icon: "📅",
      autoHide: false,
      actions: [
        {
          label: "Voir les détails",
          action: () => this.viewReservation(reservationData.id)
        },
        {
          label: "Ajouter au calendrier",
          action: () => this.addToCalendar(reservationData)
        }
      ]
    });
  }

  paymentSuccess(paymentData) {
    return this.addNotification({
      type: "success",
      title: "Paiement confirmé",
      message: `Paiement de ${paymentData.amount}€ effectué avec succès`,
      icon: "💳",
      autoHide: true,
      duration: 8000,
      actions: [
        {
          label: "Télécharger la facture",
          action: () => this.downloadReceipt(paymentData.receiptUrl)
        }
      ]
    });
  }

  artisanNearby(artisanData) {
    return this.addNotification({
      type: "info",
      title: "Artisan en route",
      message: `${artisanData.name} arrive dans ${artisanData.eta} minutes`,
      icon: "🚗",
      autoHide: false,
      actions: [
        {
          label: "Suivre en temps réel",
          action: () => this.trackArtisan(artisanData.id)
        },
        {
          label: "Contacter",
          action: () => this.contactArtisan(artisanData.phone)
        }
      ]
    });
  }

  appointmentReminder(reservationData) {
    return this.addNotification({
      type: "warning",
      title: "Rappel de rendez-vous",
      message: `Votre rendez-vous avec ${reservationData.artisan} est dans 1 heure`,
      icon: "⏰",
      autoHide: false,
      actions: [
        {
          label: "Confirmer",
          action: () => this.confirmAppointment(reservationData.id)
        },
        {
          label: "Reporter",
          action: () => this.rescheduleAppointment(reservationData.id)
        }
      ]
    });
  }

  // Marquer comme lu
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.notifyListeners();
    }
  }

  // Supprimer une notification
  removeNotification(notificationId) {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.notifyListeners();
  }

  // Effacer toutes les notifications
  clearAll() {
    this.notifications = [];
    this.notifyListeners();
  }

  // Obtenir toutes les notifications
  getNotifications() {
    return this.notifications;
  }

  // Obtenir les non lues
  getUnreadNotifications() {
    return this.notifications.filter(n => !n.read);
  }

  // Obtenir le nombre de non lues
  getUnreadCount() {
    return this.getUnreadNotifications().length;
  }

  // S'abonner aux changements
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notifier les listeners
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.notifications));
  }

  // Actions spécifiques
  viewReservation(reservationId) {
    console.log("Voir réservation:", reservationId);
    // Rediriger vers la page de détail de la réservation
  }

  addToCalendar(reservationData) {
    // Créer un lien de calendrier Google/Outlook
    const startDate = new Date(reservationData.date);
    const endDate = new Date(startDate.getTime() + (reservationData.duration || 60) * 60000);
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(reservationData.service)}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(`Rendez-vous avec ${reservationData.artisan}`)}&location=${encodeURIComponent(reservationData.address || '')}`;
    
    window.open(calendarUrl, '_blank');
  }

  downloadReceipt(receiptUrl) {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  }

  trackArtisan(artisanId) {
    console.log("Suivre artisan:", artisanId);
    // Ouvrir une carte de suivi en temps réel
  }

  contactArtisan(phone) {
    window.location.href = `tel:${phone}`;
  }

  confirmAppointment(reservationId) {
    console.log("Confirmer rendez-vous:", reservationId);
    // API call pour confirmer
  }

  rescheduleAppointment(reservationId) {
    console.log("Reporter rendez-vous:", reservationId);
    // Ouvrir le modal de reprogrammation
  }

  // Notifications push (simulation)
  requestPermission() {
    if ("Notification" in window) {
      return Notification.requestPermission();
    }
    return Promise.resolve("denied");
  }

  sendPushNotification(title, message, icon = "/logo192.png") {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, {
        body: message,
        icon: icon,
        badge: icon,
        tag: "saas-artisans"
      });
    }
  }

  // Initialiser avec des notifications d'accueil réalistes
  initWelcomeNotifications() {
    // Notification de bienvenue pour nouveaux visiteurs
    this.addNotification({
      type: "info",
      title: "Nouveau sur LYS Artisans ?",
      message: "Découvrez comment nous révolutionnons la gestion d'entreprise artisanale",
      icon: "👋",
      autoHide: false,
      actions: [
        {
          label: "VOIR LA DÉMO",
          action: () => document.querySelector('[data-demo-video]')?.click()
        },
        {
          label: "S'INSCRIRE GRATUITEMENT",
          action: () => window.location.href = "/inscription"
        }
      ]
    });

    // Offre de lancement 
    this.addNotification({
      type: "success", 
      title: "Offre de lancement",
      message: "Premiers mois GRATUITS pour tout nouvel artisan inscrit avant fin octobre !",
      icon: "🚀",
      autoHide: false,
      actions: [
        {
          label: "PROFITER DE L'OFFRE",
          action: () => window.location.href = "/inscription-electriciens"
        }
      ]
    });
  }

  // Simulation d'événements en temps réel pour la démo
  simulateRealTimeEvents() {
    // Initialiser les notifications d'accueil
    this.initWelcomeNotifications();

    // Simulation d'une notification d'activité récente
    setTimeout(() => {
      this.addNotification({
        type: "success",
        title: "Nouveau membre",
        message: "🎉 Pierre (Électricien) vient de rejoindre LYS Artisans à Lyon !",
        icon: "⚡",
        autoHide: true,
        duration: 8000,
        actions: [
          {
            label: "VOIR SON PROFIL",
            action: () => console.log("Profil consulté")
          }
        ]
      });
    }, 5000);

    // Simulation d'activité plateforme
    setTimeout(() => {
      this.addNotification({
        type: "info",
        title: "Activité en cours",
        message: "12 interventions programmées aujourd'hui sur votre secteur",
        icon: "📈",
        autoHide: true,
        duration: 6000,
        actions: [
          {
            label: "VOIR LA CARTE",
            action: () => console.log("Carte consultée")
          }
        ]
      });
    }, 10000);

    // Simulation d'un témoignage client
    setTimeout(() => {
      this.addNotification({
        type: "success",
        title: "Nouveau témoignage",
        message: "⭐⭐⭐⭐⭐ 'Service exceptionnel, je recommande !' - Sophie M.",
        icon: "�",
        autoHide: true,
        duration: 7000,
        actions: [
          {
            label: "LIRE TOUS LES AVIS",
            action: () => window.location.href = "/#temoignages"
          }
        ]
      });
    }, 18000);
  }
}

// Instance singleton
const notificationService = new NotificationService();

export default notificationService;